import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useSettings } from '@/composables';

export const useMapStore = defineStore('map', () => {
  // Read inside the store rather than at module scope: `useSettings` snapshots
  // `window.settings` where it is called, so reading it at import time tied the map
  // centre to the layout happening to write the settings before the bundle loads -
  // which it does, but nothing enforces it and no test could set a centre.
  const { getSetting } = useSettings();

  /**
   * The map centre from the application settings. Both coordinates are emitted by the
   * authenticated layout, so the fallback only applies to a page that shows a map without
   * them - where there would be no meaningful centre to use either way.
   */
  const cityCentre = computed<google.maps.LatLngLiteral>(() => ({
    lat: getSetting('city_centre_lat') ?? 0,
    lng: getSetting('city_centre_lng') ?? 0,
  }));

  const infoWindowOpen = ref(false);
  const currentInfoWinMarker = ref();
  const infoContent = ref('');
  const infoOptions = ref<any>({
    position: cityCentre.value,

    pixelOffset: {
      width: 0,
      height: -35,
    },
  });

  /**
   * Builds a Google Maps directions link for a marker position.
   *
   * Coordinates arrive from a marker position, which may not be set yet;
   * without both there is nowhere to send the user, so there is no link to
   * give rather than one pointing at `undefined,undefined`.
   */
  function getDirectionsUrl(lat?: number, lng?: number): string | undefined {
    if (lat == null || lng == null) {
      return undefined;
    }

    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }

  return {
    cityCentre,
    infoWindowOpen,
    currentInfoWinMarker,
    infoContent,
    infoOptions,
    getDirectionsUrl,
  };
});
